import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, Wifi, Wrench } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import PageSEO from '../components/PageSEO';
import StructuredData from '../components/StructuredData';

const DASHBOARD_IMAGE = "https://customer-assets.emergentagent.com/job_preview-suite/artifacts/xnqeg2qf_ChatGPT%20Image%20Mar%2029%2C%202026%2C%2012_57_41%20PM.png";

const vehicleImages = [
  'https://images.pexels.com/photos/18572796/pexels-photo-18572796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.pexels.com/photos/34421458/pexels-photo-34421458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.pexels.com/photos/9260191/pexels-photo-9260191.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.unsplash.com/photo-1670509295484-df0c2512fec4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxzZW1pJTIwdHJ1Y2slMjBmcm9udCUyMHZpZXclMjBuaWdodCUyMGxpZ2h0cyUyMGhpZ2h3YXl8ZW58MHx8fHwxNzc1MDYwNTIyfDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1568680870491-590cd4e224ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxKb2huJTIwRGVlcmUlMjB0cmFjdG9yJTIwZmllbGQlMjBzdW5zZXQlMjBnb2xkZW4lMjBob3VyfGVufDB8fHx8MTc3NTA2MDUyM3ww&ixlib=rb-4.1.0&q=85',
];

const HomePage = () => {
  const { t } = useLanguage();

  const vehicleCategories = [
    { name: t('home.cars'), image: vehicleImages[0] },
    { name: t('home.bikes'), image: vehicleImages[1] },
    { name: t('home.marine'), image: vehicleImages[2] },
    { name: t('home.trucks'), image: vehicleImages[3] },
    { name: t('home.agriculture'), image: vehicleImages[4] },
  ];

  const modules = [
    { id: 'file-services', title: t('home.moduleFileServices'), description: t('home.moduleFileServicesDesc'), icon: FileText, color: '#FF5E00' },
    { id: 'remote-diagnostics', title: t('home.moduleRemoteDiag'), description: t('home.moduleRemoteDiagDesc'), icon: Wifi, color: '#00D4FF' },
    { id: 'tools-software', title: t('home.moduleToolsSoftware'), description: t('home.moduleToolsSoftwareDesc'), icon: Wrench, color: '#FF5E00' },
  ];

  const whatIsItems = [
    t('home.whatIsItem1'),
    t('home.whatIsItem2'),
    t('home.whatIsItem3'),
    t('home.whatIsItem4'),
  ];

  return (
    <div className="relative" data-testid="home-page">
      <PageSEO path="/" />
      <StructuredData path="/" />
      <div className="ambient-glow ambient-orange" />
      <div className="ambient-glow ambient-blue" />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 pb-12" data-testid="hero-section">
        <div className="absolute top-20 left-0 right-0 glow-line" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Side - Staggered Vehicle Cards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative hidden lg:block"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-3 mt-10">
                  {vehicleCategories.slice(0, 2).map((category, index) => (
                    <motion.div
                      key={category.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="glass-card rounded-xl overflow-hidden group cursor-pointer"
                    >
                      <div className="relative h-28 overflow-hidden">
                        <img src={category.image} alt={category.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
                      </div>
                      <div className="p-3 text-center">
                        <p className="font-heading font-bold text-white text-base">{category.name}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="space-y-3">
                  {vehicleCategories.slice(2, 5).map((category, index) => (
                    <motion.div
                      key={category.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      className="glass-card rounded-xl overflow-hidden group cursor-pointer"
                    >
                      <div className="relative h-28 overflow-hidden">
                        <img src={category.image} alt={category.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
                      </div>
                      <div className="p-3 text-center">
                        <p className="font-heading font-bold text-white text-base">{category.name}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="absolute -inset-8 bg-gradient-to-r from-[#FF5E00]/10 to-[#00D4FF]/10 rounded-3xl blur-3xl -z-10" />
            </motion.div>

            {/* Right Side - Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <p className="text-sm md:text-base font-semibold text-[#00D4FF] tracking-widest uppercase mb-6">
                {t('home.heroLabel')}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-black tracking-tight mb-6">
                <span className="gradient-text">{t('home.heroTitle1')}</span>
                <br />
                <span className="gradient-text-orange">{t('home.heroTitle2')}</span>
                <br />
                <span className="gradient-text">{t('home.heroTitle3')}</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-400 max-w-xl mb-6">
                {t('home.heroSubtext')}
              </p>
              <p className="text-sm text-zinc-500 mb-8">
                {t('home.heroSupporting')} <span className="text-[#FF5E00] font-semibold">{t('home.cars')}</span>, <span className="text-[#FF5E00] font-semibold">{t('home.bikes')}</span>, <span className="text-[#FF5E00] font-semibold">{t('home.marine')}</span>, <span className="text-[#FF5E00] font-semibold">{t('home.trucks')}</span> & <span className="text-[#FF5E00] font-semibold">{t('home.agriculture')}</span>
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                <a href="https://portal.rteqsolutions.com/register" target="_blank" rel="noopener noreferrer" className="glow-button px-8 py-4 text-base md:text-lg w-full sm:w-auto text-center" data-testid="hero-get-started-button">
                  {t('home.heroCta')}
                </a>
                <a href="https://portal.rteqsolutions.com" target="_blank" rel="noopener noreferrer" className="secondary-button px-8 py-4 text-base md:text-lg w-full sm:w-auto text-center" data-testid="hero-access-suite-button">
                  {t('home.heroAccess')}
                </a>
              </div>
              
              {/* Mobile Vehicle Cards */}
              <div className="grid grid-cols-3 gap-3 mt-10 lg:hidden">
                {vehicleCategories.slice(0, 3).map((category) => (
                  <div key={category.name} className="glass-card rounded-lg overflow-hidden">
                    <div className="relative h-20 overflow-hidden">
                      <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
                    </div>
                    <div className="p-2 text-center">
                      <p className="font-heading font-bold text-white text-xs">{category.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Modules Section */}
      <section className="section-padding bg-[#05050A]" data-testid="core-modules-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-sm font-semibold text-[#FF5E00] tracking-widest uppercase mb-4">{t('home.modulesLabel')}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">{t('home.modulesTitle')}</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-base md:text-lg">{t('home.modulesDesc')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <motion.div key={module.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card rounded-2xl p-8 group cursor-pointer" data-testid={`module-card-${module.id}`}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110" style={{ background: `linear-gradient(135deg, ${module.color}20, ${module.color}10)`, boxShadow: `0 0 20px ${module.color}20` }}>
                  <module.icon className="w-7 h-7 icon-glow" style={{ color: module.color }} />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{module.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{module.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-16">
            <p className="text-zinc-400 mb-6">{t('home.modulesCta')}</p>
            <a href="https://portal.rteqsolutions.com/register" target="_blank" rel="noopener noreferrer" className="glow-button px-8 py-4 text-base inline-block" data-testid="modules-cta-button">{t('home.modulesCtaBtn')}</a>
          </motion.div>
        </div>
      </section>

      {/* What is RTEQ Portal Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] via-[#080810] to-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative order-2 lg:order-1">
              <div className="relative">
                <img src={DASHBOARD_IMAGE} alt="RTEQ Portal Dashboard" className="w-full rounded-2xl shadow-2xl" />
                <div className="absolute -inset-4 bg-gradient-to-r from-[#FF5E00]/20 to-[#00D4FF]/20 rounded-3xl blur-2xl -z-10" />
              </div>
            </motion.div>

            <div className="glass-card rounded-2xl p-8 md:p-10 order-1 lg:order-2">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">{t('home.whatIsTitle')}</h2>
              <p className="text-zinc-400 text-lg mb-8">{t('home.whatIsDesc')}</p>
              <ul className="space-y-4 mb-8">
                {whatIsItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#FF5E00]" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/suite" className="glow-button px-6 py-3 text-sm text-center">{t('home.whatIsLearnMore')}</Link>
                <Link to="/services" className="secondary-button px-6 py-3 text-sm text-center">{t('home.whatIsViewServices')}</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
